import { zodResolver } from "@hookform/resolvers/zod";
import { ListTree } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { AppShell } from "@/components/AppShell";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage } from "@/lib/api";
import { useCategories, useCreateCategory } from "@/hooks/api/flows";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const schema = z.object({
  name: z.string().min(1, "Required"),
  slug: z.string().min(1, "Required"),
});
type FormValues = z.infer<typeof schema>;

export default function CategoriesPage() {
  const { data, isLoading, error } = useCategories();
  const createCategory = useCreateCategory();
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", slug: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await createCategory.mutateAsync(values);
      toast.success("Category created.");
      form.reset({ name: "", slug: "" });
      setSlugTouched(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't create that category."));
    }
  };

  return (
    <AppShell>
      <h1 className="m-0 text-2xl font-bold text-slate-900">Categories</h1>
      <p className="mt-1 text-sm text-slate-600">
        Group flows by situation (e.g. "Billing Disputes", "Cancellations") so staff can browse
        by topic.
      </p>

      <div className="mt-6 grid gap-6 md:grid-cols-[1fr_320px]">
        <div>
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          )}
          {error && <p className="text-sm text-danger">Couldn't load categories.</p>}
          {!isLoading && data && data.results.length === 0 && (
            <EmptyState
              icon={ListTree}
              title="No categories yet"
              description="Create one to start grouping scripts."
            />
          )}
          {!isLoading && data && data.results.length > 0 && (
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-surface">
              {data.results.map((cat) => (
                <li key={cat.id} className="px-4 py-3">
                  <div className="font-medium text-slate-900">{cat.name}</div>
                  <div className="text-xs text-slate-500">/{cat.slug}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">New category</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            if (!slugTouched) {
                              form.setValue("slug", slugify(e.target.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            setSlugTouched(true);
                            field.onChange(e);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" loading={createCategory.isPending}>
                  Create category
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
